import { createContext, useCallback, useContext, useEffect, useMemo, useReducer } from 'react';

export type DetailPanelKind = 'profile' | 'event' | 'group' | 'post' | 'chat' | 'notifications';

export interface DetailPanelEntry {
  kind: DetailPanelKind;
  id: string | number;
}

interface DetailPanelContextValue {
  current: DetailPanelEntry | null;
  stack: DetailPanelEntry[];
  open: (entry: DetailPanelEntry) => void;
  push: (entry: DetailPanelEntry) => void;
  back: () => void;
  close: () => void;
}

const DetailPanelContext = createContext<DetailPanelContextValue | null>(null);

function entryFromUrl(): DetailPanelEntry | null {
  if (typeof window === 'undefined') return null;
  const sp = new URLSearchParams(window.location.search);
  const raw = sp.get('panel');
  if (!raw) return null;
  const [kind, id] = raw.split('/');
  if (!kind || !id) return null;
  if (!['profile', 'event', 'group', 'post', 'chat', 'notifications'].includes(kind)) return null;
  return { kind: kind as DetailPanelKind, id };
}

function writeUrl(entry: DetailPanelEntry | null) {
  if (typeof window === 'undefined') return;
  const sp = new URLSearchParams(window.location.search);
  if (entry) {
    sp.set('panel', `${entry.kind}/${entry.id}`);
  } else {
    sp.delete('panel');
  }
  const next = `${window.location.pathname}${sp.toString() ? `?${sp}` : ''}${window.location.hash}`;
  window.history.replaceState(null, '', next);
}

type Action =
  | { type: 'open'; entry: DetailPanelEntry }
  | { type: 'push'; entry: DetailPanelEntry }
  | { type: 'back' }
  | { type: 'close' }
  | { type: 'set'; stack: DetailPanelEntry[] };

function reducer(state: DetailPanelEntry[], action: Action): DetailPanelEntry[] {
  switch (action.type) {
    case 'open':
      return [action.entry];
    case 'push':
      return [...state, action.entry];
    case 'back':
      return state.slice(0, -1);
    case 'close':
      return [];
    case 'set':
      return action.stack;
    default:
      return state;
  }
}

export function DetailPanelProvider({ children }: { children: React.ReactNode }) {
  const initial = (() => {
    const e = entryFromUrl();
    return e ? [e] : [];
  })();
  const [stack, dispatch] = useReducer(reducer, initial);

  const current = stack[stack.length - 1] || null;

  useEffect(() => {
    writeUrl(current);
  }, [current]);

  useEffect(() => {
    const handler = () => {
      const next = entryFromUrl();
      dispatch({ type: 'set', stack: next ? [next] : [] });
    };
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, []);

  const open = useCallback((entry: DetailPanelEntry) => {
    dispatch({ type: 'open', entry });
  }, []);

  const push = useCallback((entry: DetailPanelEntry) => {
    dispatch({ type: 'push', entry });
  }, []);

  const back = useCallback(() => {
    dispatch({ type: 'back' });
  }, []);

  const close = useCallback(() => {
    dispatch({ type: 'close' });
  }, []);

  useEffect(() => {
    if (!current) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [current, close]);

  const value = useMemo<DetailPanelContextValue>(
    () => ({ current, stack, open, push, back, close }),
    [current, stack, open, push, back, close]
  );

  return <DetailPanelContext.Provider value={value}>{children}</DetailPanelContext.Provider>;
}

export function useDetailPanel(): DetailPanelContextValue {
  const ctx = useContext(DetailPanelContext);
  if (!ctx) {
    // Safe no-op when outside the provider (e.g. mobile path).
    return {
      current: null,
      stack: [],
      open: () => {},
      push: () => {},
      back: () => {},
      close: () => {},
    };
  }
  return ctx;
}
